import { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLazyFetchUserPreferencesQuery } from '../services/userPreferences.api';
import { FetchChargerRequest, useLazyFetchChargerFeaturesQuery } from '../services/chargers.api';
import { FeatureInfo } from '../stores/types/userPreferences.interface';
import { getTheme } from '../stores/selectors/theme.selector';
import { i18n } from '../services/translation/i18n';
import { useAuth } from './useAuth';
import { useUserProfile } from './useUserProfile';
import { normalizeLocale } from '../util/locale-utility';

type ContextType = {
  userIsNotSelectedForPayGo: boolean;
  allowMultiplePortReliability: boolean;
  allowSinglePortReliability: boolean;
};

const initalState: ContextType = {
  userIsNotSelectedForPayGo: false,
  allowMultiplePortReliability: false,
  allowSinglePortReliability: false,
};

const UserPreferenceContext = createContext(initalState);

export const useUserPreference = () => {
  return useContext(UserPreferenceContext);
};

export const UserPreferenceProvider = (props: any) => {
  const { children } = props;
  const auth = useAuth();
  const chargerName = window.location.pathname.substring(1);
  const chargerNameAsParam = new URLSearchParams(window.location.search).get('charger');
  const [userIsNotSelectedForPayGo, setUserIsNotSelectedForPayGo] = useState();
  const [triggerFetchUserPreferences, { data, isSuccess }] = useLazyFetchUserPreferencesQuery();

  const [triggerChargerFeatures, { data: feature }] = useLazyFetchChargerFeaturesQuery();

  const getFeatureFlagsMap = useMemo(() => {
    const featureFlagsMap: { [key: string]: boolean } = {};

    data?.features?.forEach((feature: FeatureInfo) => {
      featureFlagsMap[feature.featureName] = feature.enabled;
    });

    feature?.entities?.forEach((feature: FeatureInfo) => {
      featureFlagsMap[feature.featureName] = feature.enabled;
    });

    return featureFlagsMap;
  }, [data, feature]);

  useEffect(() => {
    if (isSuccess) {
      data?.cohort && setUserIsNotSelectedForPayGo(JSON.parse(data.cohort).isNotPayGo);
    }
  }, [isSuccess, data?.cohort]);

  useEffect(() => {
    if (auth.user) {
      triggerFetchUserPreferences();
    }
  }, [auth, triggerFetchUserPreferences]);

  const { getUserLanguage } = useUserProfile();
  const theme = useSelector(getTheme);
  const userLanguage = getUserLanguage();

  useEffect(() => {
    const browserLanguage = navigator.languages?.[0] || navigator.language;
    const lang = normalizeLocale(userLanguage || theme?.default_language || browserLanguage);
    i18n.changeLanguage(lang);
  }, [userLanguage, theme?.default_language]);

  useEffect(() => {
    const ifNotChargerPath = chargerName.split('/'); // For routes other than charger like login, register etc
    if (chargerName || chargerNameAsParam) {
      triggerChargerFeatures({
        chargerName:
          chargerName && ifNotChargerPath.length === 1 ? chargerName : chargerNameAsParam,
      } as FetchChargerRequest);
    }
  }, [chargerName, chargerNameAsParam, triggerChargerFeatures]);

  return (
    <UserPreferenceContext.Provider
      value={{
        userIsNotSelectedForPayGo,
        allowMultiplePortReliability:
          getFeatureFlagsMap.Y2025_driver_webapp_reliability_plug_in_second_multi_port || false,
        allowSinglePortReliability:
          getFeatureFlagsMap.Y2025_driver_webapp_reliability_plug_in_second_single_port || false,
      }}
    >
      {children}
    </UserPreferenceContext.Provider>
  );
};
